
import { AIModule } from '../../types';

export const FINANCIAL_AI_MODULE_2_ALGORITHMIC_TRADING: AIModule = {
  id: 'financial-ai-algorithmic-trading',
  title: 'Algorithmic Trading Systems',
  description: 'Deep dive into building and deploying AI-powered trading algorithms.',
  duration: '6 hours',
  difficulty: 'hard',
  content: `
# Algorithmic Trading Systems

Algorithmic trading uses computer programs to execute trading strategies automatically based on predefined criteria. This module covers the design, implementation, and deployment of AI-powered trading systems.

## Types of Trading Algorithms

### 1. Trend Following Algorithms
These algorithms identify and follow market trends.

**Key Indicators:**
- Moving averages
- Momentum indicators
- Breakout patterns

### 2. Mean Reversion Algorithms
Based on the theory that prices will return to their historical average.

**Strategies:**
- Statistical arbitrage
- Pairs trading
- Price oscillator signals

### 3. Market Making Algorithms
Provide liquidity by placing both buy and sell orders.

**Characteristics:**
- Profit from bid-ask spread
- High-frequency execution
- Risk management crucial

## Advanced Trading Algorithm Implementation

\`\`\`python
import numpy as np
import pandas as pd
from sklearn.ensemble import GradientBoostingClassifier
from sklearn.preprocessing import StandardScaler
import ta  # Technical Analysis library

class AdvancedTradingSystem:
    def __init__(self, symbol, lookback_period=252):
        self.symbol = symbol
        self.lookback_period = lookback_period
        self.model = GradientBoostingClassifier(n_estimators=200, random_state=42)
        self.scaler = StandardScaler()
        self.is_trained = False
        self.position = 0  # -1: short, 0: neutral, 1: long
        self.entry_price = None
        
    def calculate_technical_indicators(self, df):
        """Calculate comprehensive technical indicators"""
        # Trend indicators
        df['sma_20'] = ta.trend.sma_indicator(df['close'], window=20)
        df['ema_12'] = ta.trend.ema_indicator(df['close'], window=12)
        df['ema_26'] = ta.trend.ema_indicator(df['close'], window=26)
        df['macd'] = ta.trend.macd_diff(df['close'])
        df['adx'] = ta.trend.adx(df['high'], df['low'], df['close'])
        
        # Momentum indicators
        df['rsi'] = ta.momentum.rsi(df['close'], window=14)
        df['stoch'] = ta.momentum.stoch(df['high'], df['low'], df['close'])
        df['williams_r'] = ta.momentum.williams_r(df['high'], df['low'], df['close'])
        
        # Volatility indicators
        df['bb_upper'], df['bb_middle'], df['bb_lower'] = ta.volatility.bollinger_bands(
            df['close'], window=20, window_dev=2
        )
        df['bb_width'] = (df['bb_upper'] - df['bb_lower']) / df['bb_middle']
        df['atr'] = ta.volatility.average_true_range(df['high'], df['low'], df['close'])
        
        # Volume indicators
        df['volume_sma'] = ta.volume.volume_sma(df['close'], df['volume'])
        df['mfi'] = ta.volume.money_flow_index(df['high'], df['low'], df['close'], df['volume'])
        
        # Price action features
        df['price_change'] = df['close'].pct_change()
        df['volatility'] = df['price_change'].rolling(20).std()
        df['range_position'] = (df['close'] - df['low']) / (df['high'] - df['low'])
        
        return df
    
    def create_features(self, df):
        """Create feature matrix for ML model"""
        df = self.calculate_technical_indicators(df)
        
        feature_columns = [
            'sma_20', 'ema_12', 'ema_26', 'macd', 'adx',
            'rsi', 'stoch', 'williams_r', 'bb_width', 'atr',
            'volume_sma', 'mfi', 'volatility', 'range_position'
        ]
        
        # Create relative features (normalized by current price)
        for col in ['sma_20', 'ema_12', 'ema_26', 'bb_upper', 'bb_lower']:
            if col in df.columns:
                df[f'{col}_ratio'] = df[col] / df['close']
                feature_columns.append(f'{col}_ratio')
        
        return df[feature_columns].fillna(method='ffill').fillna(0)
    
    def create_labels(self, df, forward_days=5, profit_threshold=0.02):
        """Create trading labels based on future returns"""
        future_max = df['high'].rolling(window=forward_days, min_periods=1).max().shift(-forward_days)
        future_min = df['low'].rolling(window=forward_days, min_periods=1).min().shift(-forward_days)
        
        current_price = df['close']
        max_profit = (future_max - current_price) / current_price
        max_loss = (current_price - future_min) / current_price
        
        # Label: 1 for buy, -1 for sell, 0 for hold
        labels = np.zeros(len(df))
        labels[max_profit > profit_threshold] = 1  # Buy signal
        labels[max_loss > profit_threshold] = -1   # Sell signal
        
        return labels
    
    def train(self, historical_data):
        """Train the trading model"""
        df = historical_data.copy()
        features = self.create_features(df)
        labels = self.create_labels(df)
        
        # Remove rows with insufficient forward data
        valid_idx = ~np.isnan(labels)
        features = features[valid_idx]
        labels = labels[valid_idx]
        
        if len(features) < 100:
            raise ValueError("Insufficient data for training")
        
        # Scale features
        features_scaled = self.scaler.fit_transform(features)
        
        # Split data (use last 20% for testing)
        split_idx = int(len(features) * 0.8)
        X_train, X_test = features_scaled[:split_idx], features_scaled[split_idx:]
        y_train, y_test = labels[:split_idx], labels[split_idx:]
        
        # Train model
        self.model.fit(X_train, y_train)
        self.is_trained = True
        
        # Calculate performance metrics
        train_accuracy = self.model.score(X_train, y_train)
        test_accuracy = self.model.score(X_test, y_test)
        
        return {
            'train_accuracy': train_accuracy,
            'test_accuracy': test_accuracy,
            'feature_importance': dict(zip(features.columns, self.model.feature_importances_))
        }
    
    def generate_signal(self, current_data):
        """Generate trading signal"""
        if not self.is_trained:
            raise ValueError("Model must be trained first")
        
        features = self.create_features(current_data)
        latest_features = self.scaler.transform(features.iloc[-1:])
        
        prediction = self.model.predict(latest_features)[0]
        probabilities = self.model.predict_proba(latest_features)[0]
        
        signal_map = {-1: 'SELL', 0: 'HOLD', 1: 'BUY'}
        
        return {
            'signal': signal_map[prediction],
            'confidence': max(probabilities),
            'raw_prediction': prediction,
            'probabilities': dict(zip([-1, 0, 1], probabilities))
        }
    
    def backtest(self, test_data, initial_capital=10000):
        """Simple backtesting framework"""
        df = test_data.copy()
        capital = initial_capital
        positions = []
        
        for i in range(len(df)):
            if i < 50:  # Need minimum data for indicators
                continue
                
            current_slice = df.iloc[:i+1]
            signal_data = self.generate_signal(current_slice)
            
            current_price = df.iloc[i]['close']
            
            if signal_data['signal'] == 'BUY' and self.position <= 0:
                # Close short position or open long
                if self.position < 0:
                    profit = (self.entry_price - current_price) / self.entry_price
                    capital *= (1 + profit)
                
                self.position = 1
                self.entry_price = current_price
                positions.append({'date': df.index[i], 'action': 'BUY', 'price': current_price})
                
            elif signal_data['signal'] == 'SELL' and self.position >= 0:
                # Close long position or open short
                if self.position > 0:
                    profit = (current_price - self.entry_price) / self.entry_price
                    capital *= (1 + profit)
                
                self.position = -1
                self.entry_price = current_price
                positions.append({'date': df.index[i], 'action': 'SELL', 'price': current_price})
        
        # Close final position
        if self.position != 0:
            final_price = df.iloc[-1]['close']
            if self.position > 0:
                profit = (final_price - self.entry_price) / self.entry_price
            else:
                profit = (self.entry_price - final_price) / self.entry_price
            capital *= (1 + profit)
        
        total_return = (capital - initial_capital) / initial_capital
        buy_hold_return = (df.iloc[-1]['close'] - df.iloc[0]['close']) / df.iloc[0]['close']
        
        return {
            'final_capital': capital,
            'total_return': total_return,
            'buy_hold_return': buy_hold_return,
            'excess_return': total_return - buy_hold_return,
            'num_trades': len(positions),
            'positions': positions
        }

# Example usage:
# trading_system = AdvancedTradingSystem('AAPL')
# results = trading_system.train(historical_data)
# signal = trading_system.generate_signal(current_data)
# backtest_results = trading_system.backtest(test_data)
\`\`\`

## Risk Management in Algorithmic Trading

### Position Sizing
Determine appropriate trade sizes based on:
- Account balance
- Risk tolerance
- Market volatility
- Strategy performance

### Stop Loss and Take Profit
Automated exit strategies to limit losses and secure profits:
- Fixed percentage stops
- Trailing stops
- Volatility-based stops
- Time-based exits

### Portfolio Risk Management
- Diversification across assets
- Correlation analysis
- Maximum drawdown limits
- Exposure limits

Algorithmic trading combines quantitative analysis, machine learning, and robust risk management to create systematic trading strategies that can operate at scale and speed impossible for human traders.
  `,
  quiz: {
    id: 'financial-ai-algorithmic-trading-quiz',
    title: 'Algorithmic Trading Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What is the primary advantage of mean reversion trading strategies?',
        type: 'multiple-choice',
        options: [
          'They always generate positive returns',
          'They profit when prices return to historical averages',
          'They require no risk management',
          'They work only in trending markets'
        ],
        correctAnswer: 1,
        explanation: 'Mean reversion strategies are based on the statistical tendency of prices to return to their historical average over time.',
        difficulty: 'hard',
        points: 20
      }
    ]
  },
  resources: [
    {
      id: 'algorithmic-trading-video',
      title: 'Algorithmic Trading Systems',
      type: 'video',
      url: 'https://example.com/algo-trading',
      description: 'Deep dive into building trading algorithms'
    }
  ]
};
